import { useSEO } from '@/hooks/useSEO';
import { useState } from "react";
import PageNavbar from "@/components/feature/PageNavbar";
import Footer from "@/pages/home/components/Footer";
import { submitForm } from "@/lib/submitForm";

const SITE_URL = (import.meta.env.VITE_SITE_URL as string) || 'https://isman.co.ke';

const WELDING_SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Gas & TIG Welding Services Nairobi Kenya",
    "url": SITE_URL + "/welding-services",
    "description": "Professional TIG, MIG and gas welding services and stainless steel fabrication in Nairobi Kenya. SS304/SS316 tanks, benches, structural steel and custom metalwork by ISMAN Engineering.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "ISMAN Engineering Company",
      "url": SITE_URL,
      "telephone": "+254724114555"
    },
    "areaServed": { "@type": "Country", "name": "Kenya" },
    "serviceType": "Welding & Metal Fabrication"
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
      { "@type": "ListItem", "position": 2, "name": "Welding Services", "item": SITE_URL + "/welding-services" }
    ]
  }
];

const offerings = [
  { icon: "ri-fire-line", image: "/images/welding/tig.webp", title: "TIG Welding", desc: "Precision Tungsten Inert Gas welding for stainless steel, aluminium and exotic alloys — used on food-grade tanks, pharmacy counters and architectural pieces." },
  { icon: "ri-flashlight-line", image: "/images/welding/mig.webp", title: "MIG/MAG Welding", desc: "High-speed MIG welding for mild steel structural fabrication — frames, supports, brackets and heavy equipment bases." },
  { icon: "ri-rocket-line", image: "/images/welding/gas.webp", title: "Gas Welding & Cutting", desc: "Oxy-acetylene gas welding and thermal cutting for on-site pipework modifications and steel plate cutting." },
  { icon: "ri-shield-check-line", image: "/images/welding/inspection.webp", title: "Weld Inspection", desc: "Visual and dye-penetrant weld inspection with certified reports for food-grade and structural applications." },
  { icon: "ri-tools-line", image: "/images/welding/stainless.webp", title: "Stainless Fabrication", desc: "Full SS304/SS316 component fabrication — tanks, hoppers, benches, cladding panels and bespoke structures." },
  { icon: "ri-building-line", image: "/images/welding/structural.webp", title: "Structural Steel", desc: "Structural mild steel fabrication — mezzanines, equipment supports, roof structures and industrial platforms." },
];

const process = [
  { step: "01", title: "Drawing Review", desc: "We review your drawings or produce fabrication sketches from site measurements and engineer's designs." },
  { step: "02", title: "Material Certification", desc: "All steel is sourced with mill certificates. Stainless grade verified before cutting to prevent contamination." },
  { step: "03", title: "Precision Fabrication", desc: "CNC plasma cutting, press braking, rolling and TIG/MIG welding in our Nairobi workshop." },
  { step: "04", title: "Finishing & QC", desc: "Mechanical polishing, passivation or powder coating. Full dimensional check before dispatch." },
];

export default function WeldingServicesPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", type: "TIG Welding — Stainless Steel", details: "" });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const data = new URLSearchParams();
    Object.entries(form).forEach(([k, v]) => data.append(k, v));
    try {
      await submitForm("welding-services", data);
    } catch (_e) { /* ignore */ }
    setSubmitting(false);
    setSubmitted(true);
  };

  useSEO({
    title: 'Gas & TIG Welding Services Nairobi Kenya | Stainless Fabrication | ISMAN',
    description: 'Professional TIG, MIG and gas welding services in Nairobi Kenya. Stainless steel SS304/SS316 fabrication — tanks, benches, structural steel and custom metalwork by ISMAN Engineering.',
    keywords: 'TIG welding Nairobi Kenya, stainless steel fabrication Kenya, MIG welding Kenya, metal fabrication Nairobi, SS304 fabrication Kenya',
    canonical: SITE_URL + '/welding-services',
    schema: WELDING_SCHEMA,
  });

  return (
    <main className="min-h-screen bg-white">
      <PageNavbar />
      <section className="relative h-[480px] flex items-end overflow-hidden">
        <img src="/images/welding/tig.webp" alt="TIG welding by ISMAN Engineering" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-6 pb-14 w-full">
          <span className="inline-block bg-orange-500 text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-md mb-4">ISMAN Engineering</span>
          <h1 className="text-white text-4xl md:text-5xl font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Gas &amp; TIG Welding Services</h1>
          <p className="text-white/75 text-base max-w-2xl">Precision welding and metal fabrication for food-grade, structural and architectural applications — delivered from our fully-equipped Nairobi workshop.</p>
        </div>
      </section>

      <section className="py-16 bg-gray-50 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-orange-500 text-xs font-bold uppercase tracking-widest">Capabilities</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2" style={{ fontFamily: "'Playfair Display', serif" }}>Welding &amp; Fabrication Services</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {offerings.map((o) => (
              <div key={o.title} className="bg-white rounded-xl border border-gray-100 hover:border-orange-200 transition-all group overflow-hidden">
                <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
                  <img src={o.image} alt={o.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                <div className="w-11 h-11 flex items-center justify-center bg-orange-50 rounded-lg mb-4 group-hover:bg-orange-500 transition-colors">
                  <i className={`${o.icon} text-orange-500 group-hover:text-white text-xl transition-colors`}></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{o.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{o.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 md:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>Project Gallery</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { seq: "stainless", label: "SS304 Tank & Platform Fabrication" },
              { seq: "structural", label: "Structural Steel Mezzanine" },
              { seq: "tig", label: "TIG Welded Pipework" },
              { seq: "mig", label: "MIG Welded Frame" },
              { seq: "gas", label: "Gas Cutting of Steel Plate" },
              { seq: "inspection", label: "Weld Inspection" },
            ].map((g) => (
              <div key={g.seq} className="relative rounded-lg overflow-hidden group h-48">
                <img src={`/images/welding/${g.seq}.webp`} loading="lazy" alt={g.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                  <span className="text-white text-sm font-semibold">{g.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-10 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>Our Fabrication Process</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((p) => (
              <div key={p.step} className="text-center">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-black text-lg">{p.step}</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{p.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="inquiry" className="py-16 px-4 md:px-6 bg-white">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>Request a Welding Quote</h2>
          <p className="text-gray-500 text-sm text-center mb-8">Describe your project and we&apos;ll respond within 1 business day.</p>
          {submitted ? (
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-8 text-center">
              <i className="ri-check-double-line text-4xl text-teal-600 mb-3 block"></i>
              <h3 className="font-bold text-gray-900 mb-1">Enquiry Received!</h3>
              <p className="text-gray-500 text-sm">Our fabrication team will be in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-isman-form>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Full Name *</label>
                  <input required name="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400" placeholder="John Mwangi" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Email *</label>
                  <input required type="email" name="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400" placeholder="john@example.com" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Phone *</label>
                  <input required name="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400" placeholder="0722 000 000" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Welding Type *</label>
                  <select required name="type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400 bg-white cursor-pointer">
                    {["TIG Welding — Stainless Steel", "TIG Welding — Aluminium", "MIG/MAG — Mild Steel", "Gas Cutting & Welding", "Full Fabrication Project"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1">Project Details</label>
                <textarea name="details" value={form.details} onChange={(e) => { if (e.target.value.length <= 500) setForm({ ...form, details: e.target.value }); }} rows={4} maxLength={500} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400 resize-none" placeholder="Describe your welding/fabrication requirements..."></textarea>
                <p className="text-xs text-gray-400 text-right mt-1">{form.details.length}/500</p>
              </div>
              <button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-white font-bold py-3 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                {submitting ? <><i className="ri-loader-4-line animate-spin"></i> Sending...</> : <><i className="ri-send-plane-line"></i> Send Enquiry</>}
              </button>
            </form>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
}
